import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, PencilSimple, CheckCircle, XCircle, FloppyDisk, X, Plus, Trash, Database, User, Cube, ClockCounterClockwise, CalendarBlank, CurrencyDollar, FileText, SignIn, Envelope } from "@phosphor-icons/react";
import api from "@/lib/api";
import ClientLogo from "@/components/ClientLogo";
import { toast } from "sonner";
import { useAuth } from "@/lib/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatTz, formatLocal, inferTimezone } from "@/lib/timezone";
import TimezoneSelect from "@/components/TimezoneSelect";
import ClientMeetingsTab from "@/pages/ClientMeetingsTab";
import ClientBillingTab from "@/pages/ClientBillingTab";
import ClientSpecsTab from "@/pages/specs/ClientSpecsTab";
import ClientLoginActivityTab from "@/pages/ClientLoginActivityTab";
import ClientProvisioningTab from "@/pages/ClientProvisioningTab";
import ExternallySentEmailsTab from "@/components/ExternallySentEmailsTab";
import ClientDigestPreview from "@/components/ClientDigestPreview";

export default function ClientOverviewPage() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const { user, hasRole, canSee } = useAuth();
  const [tab, setTab] = useState("overview");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [modules, setModules] = useState([]);
  const [licenceLimit, setLicenceLimit] = useState(0);
  const [modDirty, setModDirty] = useState(false);
  const [users, setUsers] = useState([]);
  const [usersNote, setUsersNote] = useState("");
  const [history, setHistory] = useState([]);
  const [showAddUser, setShowAddUser] = useState(false);
  const [newUser, setNewUser] = useState({ user_id: "", email: "", first_name: "", last_name: "", job_title: "", user_type: "Buyer", password: "", is_admin: false, is_licensed: true });
  // v1.58 — admin-only password reveal/edit. Stores { [user_pk]: true } for rows whose
  // plaintext password is currently revealed; and { [user_pk]: "newValue" } during edit.
  const [revealed, setRevealed] = useState({});
  const [pwDraft, setPwDraft] = useState({});
  const isAdmin = hasRole("platform_admin");
  const isInternal = user?.user_type === "internal";

  useEffect(() => { load(); }, [clientId]);

  const load = async () => {
    setLoading(true);
    try {
      const ov = await api.get(`/tenants/${clientId}/overview`);
      setData(ov.data);
      setForm(ov.data);
    } catch (e) { toast.error("Failed to load client"); }
    setLoading(false);
  };

  useEffect(() => {
    if (tab === "tenant") loadModules();
    if (tab === "users") loadUsers();
    if (tab === "history") loadHistory();
  }, [tab]);

  const loadModules = async () => {
    try {
      const res = await api.get(`/tenants/${clientId}/eba/modules`);
      setModules(res.data.modules);
      setLicenceLimit(res.data.user_license_limit || 0);
      setModDirty(false);
    } catch (e) { toast.error("Failed to load modules"); }
  };

  const loadUsers = async () => {
    try {
      const res = await api.get(`/tenants/${clientId}/eba/users`);
      setUsers(res.data.users || []);
      setUsersNote(res.data.note || "");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load users"); }
  };

  const loadHistory = async () => {
    try { const res = await api.get(`/tenants/${clientId}/history`); setHistory(res.data.events || []); }
    catch (e) { toast.error("Failed to load history"); }
  };

  const saveOverview = async () => {
    try {
      await api.put(`/tenants/${clientId}/overview`, form);
      toast.success("Saved");
      setEditing(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const toggleModule = (name) => {
    setModules(ms => ms.map(m => m.name === name ? { ...m, enabled: !m.enabled } : m));
    setModDirty(true);
  };

  const saveModules = async () => {
    try {
      const map = {}; modules.forEach(m => map[m.name] = m.enabled);
      await api.put(`/tenants/${clientId}/eba/modules`, { modules: map, user_license_limit: parseInt(licenceLimit) || 0 });
      toast.success("Modules & licence saved"); setModDirty(false); load(); loadModules();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const createUser = async () => {
    try {
      await api.post(`/tenants/${clientId}/eba/users`, newUser);
      toast.success("User created"); setShowAddUser(false); loadUsers();
      setNewUser({ user_id: "", email: "", first_name: "", last_name: "", job_title: "", user_type: "Buyer", password: "", is_admin: false, is_licensed: true });
    } catch (e) { toast.error(e.response?.data?.detail || "Create failed"); }
  };

  const savePassword = async (u) => {
    const next = (pwDraft[u.mongo_id] || "").trim();
    if (!next) { toast.error("Password cannot be empty"); return; }
    try {
      await api.put(`/tenants/${clientId}/eba/users/${u.mongo_id}`, { password: next });
      toast.success(`Password updated for ${u.email}`);
      setPwDraft(d => { const n = { ...d }; delete n[u.mongo_id]; return n; });
      loadUsers();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };

  const copyPassword = (u) => {
    if (!u.password_plaintext) { toast.error("No password on record — set one first"); return; }
    navigator.clipboard.writeText(u.password_plaintext);
    toast.success(`Copied password for ${u.email}`);
  };

  const toggleUser = async (u, key) => {
    try {
      await api.put(`/tenants/${clientId}/eba/users/${u.mongo_id}`, { [key]: !u[key] });
      loadUsers();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };

  const deactivateUser = async (u) => {
    if (!window.confirm(`Deactivate ${u.email}?`)) return;
    try { await api.delete(`/tenants/${clientId}/eba/users/${u.mongo_id}`); toast.success("Deactivated"); loadUsers(); }
    catch (e) { toast.error(e.response?.data?.detail || "Deactivate failed"); }
  };

  if (loading) return <div className="p-8 text-sm text-[#A3A3A3]">Loading...</div>;
  if (!data) return <div className="p-8 text-sm text-[#DC2626]">Client not found</div>;

  // Session 68 pt3 — only Eyvo internals (with client_mgmt:write) and
  // the tenant's own client_admin can upload/replace the logo.
  const canEditLogo = user?.user_type === "internal"
    ? canSee("client_mgmt")
    : (user?.role === "client_admin" && user?.tenant_id === clientId);

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";  // allow re-selecting the same file
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Choose an image file"); return; }
    if (file.size > 2 * 1024 * 1024) { toast.error("Logo must be under 2 MB"); return; }
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await api.post(`/mgmt/clients/${clientId}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      // Append a cache-buster query so the <img> immediately re-renders
      // even though the storage path itself is already cb'd by the server.
      setData(d => ({ ...d, logo_url: res.data.logo_url }));
      toast.success("Logo updated");
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
  };

  const handleLogoRemove = async () => {
    if (!window.confirm("Remove the client's logo?")) return;
    try {
      await api.delete(`/mgmt/clients/${clientId}/logo`);
      setData(d => ({ ...d, logo_url: null }));
      toast.success("Logo removed");
    } catch (err) { toast.error(err.response?.data?.detail || "Remove failed"); }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="client-overview-page">
      <button onClick={() => navigate("/clients")} className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-[#525252] hover:text-[#0055FF] mb-4" data-testid="back-to-clients"><ArrowLeft size={14} /> Back to Clients</button>
      <div className="flex items-center gap-5 mb-6 flex-wrap">
        {/* Session 68 pt3 — client logo box. Sized to 240×80 to match
            the landscape rectangle most company logos ship in.
            Drops a dashed placeholder when empty so the upload
            affordance is obvious. */}
        <div className="relative group" data-testid="client-logo-box">
          {data.logo_url ? (
            // 2026-07 client-logo repair sprint — ClientLogo falls back to
            // an initials box (instead of a raw broken-image icon) if this
            // client's logo_url points at a blob that 404s.
            <ClientLogo
              client={data}
              size="lg"
              className="border border-[#E5E5E5] p-2"
              testId="client-logo-img"
            />
          ) : (
            <div className="w-[240px] h-[80px] border-2 border-dashed border-[#E5E5E5] bg-[#FAFAFA] flex items-center justify-center text-[10px] font-bold uppercase tracking-[0.15em] text-[#A3A3A3]" data-testid="client-logo-empty">
              No logo yet
            </div>
          )}
          {canEditLogo && (
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
              <label className="cursor-pointer bg-white text-[10px] font-bold uppercase tracking-[0.15em] text-[#0A0A0A] px-2 py-1 hover:bg-[#F5F5F5]" data-testid="client-logo-upload-trigger">
                {data.logo_url ? "Replace" : "Upload"}
                <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" data-testid="client-logo-file-input" />
              </label>
              {data.logo_url && (
                <button onClick={handleLogoRemove} className="bg-white text-[10px] font-bold uppercase tracking-[0.15em] text-[#DC2626] px-2 py-1 hover:bg-[#FEF2F2]" data-testid="client-logo-remove-btn">
                  Remove
                </button>
              )}
            </div>
          )}
        </div>
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-3 flex-wrap">
            {/* Session 68 pt4 — this screen is the one place where
                we always display the proper legal name (the policy
                everywhere else is short_name only). When a short
                name is set we expose it as a small caption underneath
                so the operator can confirm both at a glance. */}
            <h1 className="text-2xl font-black text-[#0A0A0A] tracking-tight" data-testid="client-legal-name">{data.name}</h1>
            <span className={`text-[10px] font-bold uppercase px-2 py-0.5 ${data.status === "active" ? "bg-[#F0FDF4] text-[#16A34A]" : data.status === "inactive" ? "bg-[#FFFBEB] text-[#D97706]" : "bg-[#F5F5F5] text-[#525252]"}`}>{data.status}</span>
            <span className={`text-[10px] font-bold uppercase px-2 py-0.5 ${(data.eba_system || "eBA1") === "eBA2" ? "bg-[#EEF2FF] text-[#4338CA]" : "bg-[#F5F5F5] text-[#525252]"}`} data-testid="eba-system-badge">{data.eba_system || "eBA1"}</span>
            {data.company_code && <span className="text-xs text-[#A3A3A3]">· {data.company_code}</span>}
          </div>
          {data.short_name && data.short_name !== data.name && (
            <p className="text-xs text-[#525252]" data-testid="client-short-name-caption">
              Internal short name: <span className="font-bold text-[#0A0A0A]">{data.short_name}</span>
            </p>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-[#E5E5E5] mb-6">
        {[
          { k: "overview", label: "Overview", icon: <User size={14} /> },
          // Pulse UI consolidation ruling (2026-08-18): this is the single,
          // client-scoped EBA management surface — legacy eBA1 usage/API-key/
          // module data (unchanged, still owned by `clients` fields) AND the
          // Track 2 EBA 2.0 control-plane (tenant/environment provisioning,
          // System Type, licences, module & function entitlements) both
          // render inside this one tab. There is deliberately no second,
          // separate "Provisioning" tab and no standalone global EBA 2.0
          // Tenants page (see Sidebar.js / App.js) — one authoritative UX.
          { k: "tenant", label: "EBA Provisioning", icon: <Cube size={14} /> },
          { k: "users", label: "eBA Users", icon: <Database size={14} /> },
          { k: "meetings", label: "Meetings", icon: <CalendarBlank size={14} /> },
          { k: "emails", label: "Externally Sent Emails", icon: <Envelope size={14} /> },
          // Specs tab — visible to anyone with spec_writing read/write.
          // Hides cleanly for users whose matrix has it hidden.
          ...(canSee("spec_writing") ? [{ k: "specs", label: "Specs", icon: <FileText size={14} /> }] : []),
          // User logins is intentionally internal-only — clients
          // shouldn't see who logged in from where, only Eyvo staff.
          ...(isInternal ? [{ k: "logins", label: "User Logins", icon: <SignIn size={14} /> }] : []),
          // Billing/renewals — Session 69: gated on the new `billing`
          // module permission, not just hard-coded to platform_admin
          // role. Currently only Mike has it (platform_admin auto-grants
          // every module; delivery_admin is explicitly excluded from
          // billing). Future operators can be granted access on the
          // Team page without needing a code change.
          ...(canSee("billing") ? [{ k: "billing", label: "Billing", icon: <CurrencyDollar size={14} /> }] : []),
          { k: "history", label: "History", icon: <ClockCounterClockwise size={14} /> },
        ].map(t => (
          <button key={t.k} onClick={() => setTab(t.k)} className={`px-4 py-2.5 text-xs font-bold uppercase tracking-wider flex items-center gap-2 border-b-2 transition-colors ${tab === t.k ? "border-[#0055FF] text-[#0055FF]" : "border-transparent text-[#525252] hover:text-[#0A0A0A]"}`} data-testid={`tab-${t.k}`}>
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {/* Overview */}
      {tab === "overview" && (
        <div>
          <div className="flex justify-end mb-4 gap-2">
            {isAdmin && !editing && data.status !== "archived" && (
              <Button onClick={async () => {
                if (!window.confirm(`Archive ${data.name}? Archived clients are hidden from the default view but can be restored later.`)) return;
                try {
                  await api.put(`/tenants/${clientId}/archive`);
                  toast.success("Client archived");
                  navigate("/clients");
                } catch (e) { toast.error(e.response?.data?.detail || "Archive failed"); }
              }} variant="outline" className="rounded-none border-[#E5E5E5] text-[#525252]" data-testid="archive-client-btn">
                Archive
              </Button>
            )}
            {isAdmin && !editing && data.status === "archived" && (
              <Button onClick={async () => {
                try {
                  await api.put(`/tenants/${clientId}/unarchive`);
                  toast.success("Client restored");
                  load();
                } catch (e) { toast.error(e.response?.data?.detail || "Restore failed"); }
              }} variant="outline" className="rounded-none border-[#16A34A] text-[#16A34A]" data-testid="unarchive-client-btn">
                Restore
              </Button>
            )}
            {isAdmin && !editing && (
              <Button onClick={async () => {
                if (!window.confirm(`Permanently delete ${data.name}? This cannot be undone.`)) return;
                try {
                  await api.delete(`/tenants/${clientId}`);
                  toast.success("Client deleted");
                  navigate("/clients");
                } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
              }} variant="outline" className="rounded-none border-[#DC2626] text-[#DC2626] hover:bg-[#FEF2F2]" data-testid="delete-client-btn">
                Delete
              </Button>
            )}
            {isAdmin && !editing && <Button onClick={() => setEditing(true)} className="bg-[#0055FF] hover:bg-[#0040CC] text-white rounded-none" data-testid="edit-overview-btn"><PencilSimple size={14} className="mr-1.5" /> Edit</Button>}
            {editing && (
              <>
                <Button onClick={() => { setEditing(false); setForm(data); }} variant="outline" className="rounded-none" data-testid="cancel-overview"><X size={14} className="mr-1.5" /> Cancel</Button>
                <Button onClick={saveOverview} className="bg-[#16A34A] hover:bg-[#15803D] text-white rounded-none" data-testid="save-overview"><FloppyDisk size={14} className="mr-1.5" /> Save</Button>
              </>
            )}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_1fr_1fr] gap-x-4 gap-y-2 border border-[#E5E5E5] p-4 bg-[#FAFAFA]">
            {/* ── Column 1 — Company & Address ── */}
            <Col>
              <F label="Company Name" value={form.name} editing={editing} onChange={v => setForm({ ...form, name: v })} />
              <F label="Client Short Name" value={form.short_name} editing={editing} onChange={v => setForm({ ...form, short_name: v })} placeholder={form.name || "(defaults to Company Name)"} />
              <F label="Company Code" value={form.company_code} editing={editing} onChange={v => setForm({ ...form, company_code: v })} />
              <F label="Client Status" value={form.status} editing={editing} onChange={v => setForm({ ...form, status: v })} options={["active","inactive","archived","pending"]} />
              <F label="Address Line 1" value={form.address_line_1} editing={editing} onChange={v => setForm({ ...form, address_line_1: v })} />
              <F label="Address Line 2" value={form.address_line_2} editing={editing} onChange={v => setForm({ ...form, address_line_2: v })} />
              <F label="City" value={form.city} editing={editing} onChange={v => setForm({ ...form, city: v })} />
              <F label="State / Region" value={form.state_region} editing={editing} onChange={v => setForm({ ...form, state_region: v })} />
              <F label="Postal Code" value={form.postal_code} editing={editing} onChange={v => setForm({ ...form, postal_code: v })} />
              <F label="Country" value={form.country} editing={editing} onChange={v => setForm({ ...form, country: v })} />
              {/* Session 68 pt4 — timezone now ALWAYS shows the
                  effective tz (explicit OR inferred from
                  country/state) with a small subtitle clarifying
                  the source. The dropdown is editable in edit mode
                  and seeds with the inferred default when no
                  explicit tz has been saved. */}
              {(() => {
                const inferred = inferTimezone({ country: form.country, state_region: form.state_region });
                const effective = form.timezone || inferred;
                const isInferred = !form.timezone && !!inferred;
                return (
                  <div className="flex items-start gap-2 mt-1">
                    <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#525252] py-2 px-0 w-[140px] flex-shrink-0">Timezone</div>
                    <div className="flex-1 py-1">
                      {editing ? (
                        <>
                          <TimezoneSelect
                            value={form.timezone || inferred}
                            onChange={(v) => setForm({ ...form, timezone: v })}
                            testId="client-timezone-select"
                          />
                          <p className="text-[10px] text-[#A3A3A3] mt-1">
                            {form.timezone
                              ? "Manually set — clear the dropdown to fall back to inferred."
                              : inferred
                                ? `Inferred from ${form.country || "country"}${form.state_region ? ` / ${form.state_region}` : ""} — adjust if the office sits in a different zone.`
                                : "No country/state set — pick a timezone manually."}
                          </p>
                        </>
                      ) : (
                        <>
                          <span className={effective ? "text-sm font-bold text-[#0A0A0A]" : "text-sm text-[#A3A3A3] italic"} data-testid="client-timezone-effective">
                            {effective || "—"}
                          </span>
                          {isInferred && (
                            <p className="text-[10px] text-[#A3A3A3] mt-0.5">inferred from country/state</p>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                );
              })()}
            </Col>

            {/* ── Column 2 — Contacts (main / billing / PM) ── */}
            <Col>
              <div className="text-[9px] font-bold uppercase tracking-[0.2em] text-[#0A0A0A] mt-0.5 mb-0.5">Main Contact</div>
              <NameSplit
                firstValue={form.contact_first_name}
                lastValue={form.contact_last_name}
                editing={editing}
                onChangeFirst={v => setForm({ ...form, contact_first_name: v })}
                onChangeLast={v => setForm({ ...form, contact_last_name: v })}
                testIdPrefix="contact"
              />
              <F label="Email" value={form.contact_email} editing={editing} onChange={v => setForm({ ...form, contact_email: v })} />
              <F label="Phone" value={form.contact_phone} editing={editing} onChange={v => setForm({ ...form, contact_phone: v })} />
              <div className="text-[9px] font-bold uppercase tracking-[0.2em] text-[#0055FF] mt-3 mb-0.5">Billing Contact</div>
              <NameSplit
                firstValue={form.billing_first_name}
                lastValue={form.billing_last_name}
                editing={editing}
                onChangeFirst={v => setForm({ ...form, billing_first_name: v })}
                onChangeLast={v => setForm({ ...form, billing_last_name: v })}
                testIdPrefix="billing"
              />
              <F label="Email" value={form.billing_email} editing={editing} onChange={v => setForm({ ...form, billing_email: v })} />
              <F label="Phone" value={form.phone} editing={editing} onChange={v => setForm({ ...form, phone: v })} />
              <div className="text-[9px] font-bold uppercase tracking-[0.2em] text-[#7C3AED] mt-3 mb-0.5">Project Management Contact</div>
              <NameSplit
                firstValue={form.pm_first_name}
                lastValue={form.pm_last_name}
                editing={editing}
                onChangeFirst={v => setForm({ ...form, pm_first_name: v })}
                onChangeLast={v => setForm({ ...form, pm_last_name: v })}
                testIdPrefix="pm"
              />
              <F label="Email" value={form.pm_contact_email} editing={editing} onChange={v => setForm({ ...form, pm_contact_email: v })} />
              <F label="Phone" value={form.pm_contact_phone} editing={editing} onChange={v => setForm({ ...form, pm_contact_phone: v })} />
              <F label="Entry Date" value={data.created_at ? formatTz(data.created_at, user?.timezone) : "—"} editing={false} />
            </Col>

            {/* ── Column 3 — System, Licensing, Security & Contract dates ──
                Session 68 pt4 — moved Contract dates from Col 2 down
                here under Security (per Mike's "dates at the bottom
                look more even" feedback). The third column was much
                shorter than the first two; pulling the contract dates
                across balances the columns and lets us close the
                horizontal gap to the second column too. */}
            <Col>
              <div>
                <Label className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#525252]">System Type</Label>
                {editing ? (
                  <select
                    value={form.eba_system || "eBA1"}
                    onChange={e => setForm({ ...form, eba_system: e.target.value })}
                    className="w-full mt-1 h-9 px-3 text-sm border border-[#E5E5E5] bg-white focus:outline-none focus:border-[#0055FF]"
                    data-testid="system-type-select"
                  >
                    <option value="eBA1">eBA 1.5</option>
                    <option value="eBA2">eBA 2.0</option>
                  </select>
                ) : (
                  <div className={`text-sm mt-1 font-bold ${(data.eba_system || "eBA1") === "eBA2" ? "text-[#4338CA]" : "text-[#0A0A0A]"}`}>
                    {(data.eba_system || "eBA1") === "eBA2" ? "eBA 2.0" : "eBA 1.5"}
                  </div>
                )}
              </div>
              <F label="Application URL" value={form.application_url} editing={editing} onChange={v => setForm({ ...form, application_url: v })} />
              <F label="Auth Method" value={form.auth_method} editing={editing} onChange={v => setForm({ ...form, auth_method: v })} />
              <F label="SQL DB Name" value={form.eyvo_sql_db_name} editing={editing} onChange={v => setForm({ ...form, eyvo_sql_db_name: v })} />
              {(form.eba_system || "eBA1") === "eBA2" && (
                <F label="MongoDB Name" value={form.eyvo_db_name} editing={editing} onChange={v => setForm({ ...form, eyvo_db_name: v })} />
              )}
              <F label="User Licence Limit" value={form.user_license_limit} editing={editing} onChange={v => setForm({ ...form, user_license_limit: parseInt(v) || null })} type="number" />
              <F label="Active Users (live)" value={data.active_user_count == null ? "—" : data.active_user_count} editing={false} />
              <div className="text-[9px] font-bold uppercase tracking-[0.2em] text-[#D97706] mt-3 mb-0.5">Security</div>
              <F label="Allowed Login Domains" value={form.allowed_email_domains} editing={editing} onChange={v => setForm({ ...form, allowed_email_domains: v })} placeholder="example.com, vendor.co.uk" />
              <F label="Client Since" value={form.client_since} editing={editing} onChange={v => setForm({ ...form, client_since: v })} type="date" />
              <F label="Contract Expiration Date" value={form.contract_expiration_date} editing={editing} onChange={v => setForm({ ...form, contract_expiration_date: v })} type="date" />
              <F label="Current Annual Period Expiry Date" value={form.expiry_date} editing={editing} onChange={v => setForm({ ...form, expiry_date: v })} type="date" />
            </Col>
          </div>
          {isInternal && isAdmin && (
            <div className="mt-6">
              <ClientDigestPreview tenantId={clientId} tenantName={data.short_name || data.name} />
            </div>
          )}
        </div>
      )}

      {/* EBA Provisioning — single client-scoped surface (Pulse UI consolidation ruling, 2026-08-18).
          Section order follows the ruling's suggested organization: Provisioning/Tenant Status,
          System Configuration, Licences, Modules & Functions (EBA 2.0) live inside
          ClientProvisioningTab; Usage/Synchronization + Integration/API Key live in
          EbaUsagePanel; the legacy eBA1 module grid stays last and unconditional — it is not
          gated on eba2_tenants because a client entirely on EBA 1.0 Legacy still needs it. */}
      {tab === "tenant" && canSee("eba2_tenants") && (
        <ClientProvisioningTab clientId={clientId} isAdmin={isAdmin} />
      )}

      {tab === "tenant" && <EbaUsagePanel clientId={clientId} isAdmin={isAdmin} />}
      {tab === "tenant" && (
        // Session 44: every eBA1 tenant now has ``enabled_modules`` seeded
        // from the Active-Client spreadsheet, so the placeholder is no
        // longer appropriate — render the live grid regardless of which
        // eBA platform the client sits on. (Users tab still shows the
        // placeholder for eBA1 because the legacy Mongo user source is
        // not wired up yet.)
        <div className="border border-[#E5E5E5] p-5 bg-[#FAFAFA]">
          <div className="flex items-end justify-between gap-4 mb-5 flex-wrap">
            <div>
              <h2 className="text-lg font-black tracking-tight">Legacy Modules & Functions (eBA1)</h2>
              <p className="text-xs text-[#525252] mt-0.5">Toggle modules the client can access in their eBA instance.</p>
            </div>
            <div className="flex items-end gap-2">
              <div>
                <Label className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#525252]">User Licence Limit</Label>
                <Input type="number" value={licenceLimit} onChange={e => { setLicenceLimit(e.target.value); setModDirty(true); }} className="w-28 rounded-none" disabled={!isAdmin} data-testid="licence-limit-input" />
              </div>
              {isAdmin && modDirty && <Button onClick={saveModules} className="bg-[#16A34A] hover:bg-[#15803D] text-white rounded-none" data-testid="save-modules-btn"><FloppyDisk size={14} className="mr-1.5" /> Save Changes</Button>}
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
            {modules.map(m => (
              <button key={m.name} onClick={() => isAdmin && toggleModule(m.name)} disabled={!isAdmin} className={`flex items-center gap-2 border px-3 py-2.5 text-left transition-colors ${m.enabled ? "bg-[#F0FDF4] border-[#16A34A] text-[#16A34A]" : "bg-white border-[#E5E5E5] text-[#A3A3A3] hover:bg-[#F5F5F5]"}`} data-testid={`module-toggle-${m.name}`}>
                {m.enabled ? <CheckCircle size={16} weight="fill" /> : <XCircle size={16} />}
                <span className="text-xs font-bold">{m.name}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Users */}
      {tab === "users" && (
        // Session 45: dropped the eBA1-specific placeholder. The Users
        // API is platform-agnostic — eBA1 tenants now have
        // ``eyvo_db_name = eba_<code>`` set via the active-client
        // sync, so the panel renders and admins can manage users
        // directly (create/edit/deactivate go through the existing
        // Mongo-backed CRUD). Tenants without a linked DB see the
        // ``usersNote`` banner from the API.
        <div>
          <div className="flex justify-between items-center mb-4">
            <p className="text-xs text-[#525252]">{users.length} user{users.length !== 1 ? "s" : ""} · <span className="text-[10px] text-[#A3A3A3]">source: MongoDB <code>{data.eyvo_db_name || "—"}</code></span></p>
            {isAdmin && data.eyvo_db_name && <Button onClick={() => setShowAddUser(true)} className="bg-[#0055FF] hover:bg-[#0040CC] text-white rounded-none" data-testid="add-eba-user-btn"><Plus size={14} className="mr-1.5" /> Add User</Button>}
          </div>
          {usersNote && <div className="p-3 bg-[#FFFBEB] border border-[#F59E0B] text-xs text-[#92400E] mb-3">{usersNote}</div>}
          <div className="border border-[#E5E5E5] bg-white overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-[#FAFAFA] border-b border-[#E5E5E5] text-[10px] font-bold uppercase tracking-wider text-[#525252]">
                <tr>
                  <th className="px-3 py-2 text-left">User ID</th>
                  <th className="px-3 py-2 text-left">Email</th>
                  <th className="px-3 py-2 text-left">Name</th>
                  <th className="px-3 py-2 text-left">Role</th>
                  <th className="px-3 py-2 text-center">Admin</th>
                  <th className="px-3 py-2 text-center">Licensed</th>
                  {isAdmin && <th className="px-3 py-2 text-left">Password</th>}
                  <th className="px-3 py-2 text-left">Last Login</th>
                  {isAdmin && <th className="px-3 py-2"></th>}
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.mongo_id} className="border-b border-[#F5F5F5] hover:bg-[#FAFAFA]" data-testid={`eba-user-row-${u.user_id}`}>
                    <td className="px-3 py-2 font-medium">{u.user_id}</td>
                    <td className="px-3 py-2">{u.email}</td>
                    <td className="px-3 py-2">{[u.first_name, u.last_name].filter(Boolean).join(" ")}</td>
                    <td className="px-3 py-2 text-[#525252]">{u.user_type}</td>
                    <td className="px-3 py-2 text-center"><Toggle value={u.is_admin} onClick={() => isAdmin && toggleUser(u, "is_admin")} disabled={!isAdmin} /></td>
                    <td className="px-3 py-2 text-center"><Toggle value={u.is_licensed} onClick={() => isAdmin && toggleUser(u, "is_licensed")} disabled={!isAdmin} /></td>
                    {isAdmin && (
                      <td className="px-3 py-2 text-[#525252]" data-testid={`eba-user-password-${u.user_id}`}>
                        {pwDraft[u.mongo_id] !== undefined ? (
                          <div className="flex items-center gap-1">
                            <Input
                              value={pwDraft[u.mongo_id]}
                              onChange={e => setPwDraft(d => ({ ...d, [u.mongo_id]: e.target.value }))}
                              placeholder="New password"
                              className="h-7 text-xs rounded-none w-36"
                              data-testid={`eba-pw-input-${u.user_id}`}
                            />
                            <button
                              onClick={() => savePassword(u)}
                              className="text-[10px] font-bold text-[#16A34A] hover:underline"
                              data-testid={`eba-pw-save-${u.user_id}`}
                            >SAVE</button>
                            <button
                              onClick={() => setPwDraft(d => { const n = { ...d }; delete n[u.mongo_id]; return n; })}
                              className="text-[10px] text-[#A3A3A3] hover:underline"
                            >cancel</button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1.5">
                            <span className="font-mono text-[11px]">
                              {revealed[u.mongo_id]
                                ? (u.password_plaintext || <span className="italic text-[#A3A3A3]">— not set —</span>)
                                : (u.password_plaintext ? "••••••••" : <span className="italic text-[#A3A3A3]">— not set —</span>)}
                            </span>
                            <button
                              onClick={() => setRevealed(r => ({ ...r, [u.mongo_id]: !r[u.mongo_id] }))}
                              className="text-[9px] uppercase tracking-wider text-[#0055FF] hover:underline"
                              title={revealed[u.mongo_id] ? "Hide password" : "Reveal password"}
                              data-testid={`eba-pw-toggle-${u.user_id}`}
                            >{revealed[u.mongo_id] ? "hide" : "show"}</button>
                            {u.password_plaintext && (
                              <button
                                onClick={() => copyPassword(u)}
                                className="text-[9px] uppercase tracking-wider text-[#0055FF] hover:underline"
                                title="Copy to clipboard"
                                data-testid={`eba-pw-copy-${u.user_id}`}
                              >copy</button>
                            )}
                            <button
                              onClick={() => setPwDraft(d => ({ ...d, [u.mongo_id]: "" }))}
                              className="text-[9px] uppercase tracking-wider text-[#525252] hover:underline"
                              title="Set / reset password"
                              data-testid={`eba-pw-edit-${u.user_id}`}
                            >reset</button>
                          </div>
                        )}
                      </td>
                    )}
                    <td className="px-3 py-2 text-[#525252]">{u.last_login ? formatTz(u.last_login, user?.timezone) : "—"}</td>
                    {isAdmin && <td className="px-3 py-2 text-right"><button onClick={() => deactivateUser(u)} className="text-[#DC2626] hover:bg-[#FEF2F2] p-1"><Trash size={12} /></button></td>}
                  </tr>
                ))}
                {users.length === 0 && <tr><td colSpan={isAdmin ? 9 : 8} className="px-3 py-8 text-center text-[#A3A3A3]">No users found.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Meetings */}
      {tab === "meetings" && (
        <ClientMeetingsTab clientId={clientId} userTz={user?.timezone} />
      )}

      {/* Externally Sent Emails — archived BCC inbound */}
      {tab === "emails" && (
        <ExternallySentEmailsTab entityType="client" entityId={clientId} />
      )}

      {/* Billing */}
      {tab === "billing" && canSee("billing") && (
        <ClientBillingTab clientId={clientId} isAdmin={isAdmin} />
      )}

      {/* Specs */}
      {tab === "specs" && (
        <ClientSpecsTab clientId={clientId} clientName={data?.name} />
      )}

      {/* User Logins (internal-only) */}
      {tab === "logins" && isInternal && (
        <ClientLoginActivityTab clientId={clientId} />
      )}

      {/* History */}
      {tab === "history" && (
        <div className="border border-[#E5E5E5] bg-white">
          <div className="bg-[#FAFAFA] px-4 py-3 border-b border-[#E5E5E5]"><h3 className="text-sm font-bold">Interaction & Audit History</h3><p className="text-[10px] text-[#A3A3A3] mt-0.5">Every material change or interaction related to this client is recorded here.</p></div>
          <div className="max-h-[600px] overflow-y-auto">
            {history.length === 0 && <div className="p-8 text-center text-[#A3A3A3] text-xs">No history yet.</div>}
            {history.map((e, i) => (
              <div key={i} className="px-4 py-3 border-b border-[#F5F5F5] text-xs flex gap-4">
                <div className="text-[#A3A3A3] w-36 flex-shrink-0">{e.when ? formatTz(e.when, user?.timezone) : "—"}</div>
                <div className="flex-1">
                  <div className="font-bold text-[#0A0A0A]">{e.action}</div>
                  {e.details && <div className="text-[#525252] mt-0.5">{e.details}</div>}
                  <div className="text-[#A3A3A3] mt-0.5">by {e.user}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Add User Modal */}
      {showAddUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowAddUser(false)}>
          <div className="bg-white rounded-none border border-[#E5E5E5] p-6 max-w-lg w-full" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold mb-4">Add eBA User</h3>
            <div className="space-y-3">
              {[
                ["user_id","User ID"],["email","Email"],["first_name","First Name"],["last_name","Last Name"],
                ["job_title","Job Title"],["telephone","Telephone"],["mobile","Mobile"],["user_type","User Type"],
                ["password","Password (plaintext — testing only)"],
              ].map(([k, label]) => (
                <div key={k}>
                  <Label className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#525252]">{label}</Label>
                  <Input value={newUser[k] || ""} onChange={e => setNewUser({ ...newUser, [k]: e.target.value })} className="rounded-none" data-testid={`new-user-${k}`} />
                </div>
              ))}
              <div className="flex gap-4">
                <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={newUser.is_admin} onChange={e => setNewUser({ ...newUser, is_admin: e.target.checked })} /> Admin</label>
                <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={newUser.is_licensed} onChange={e => setNewUser({ ...newUser, is_licensed: e.target.checked })} /> Licensed</label>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button onClick={() => setShowAddUser(false)} variant="outline" className="rounded-none" data-testid="cancel-add-user">Cancel</Button>
                <Button onClick={createUser} className="bg-[#0055FF] hover:bg-[#0040CC] text-white rounded-none" data-testid="submit-add-user">Create User</Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Col({ children }) { return <div className="space-y-2">{children}</div>; }

function F({ label, value, editing, onChange, type, options }) {
  return (
    <div>
      <Label className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#A3A3A3] block mb-0.5">{label}</Label>
      {editing && options ? (
        <select value={value || ""} onChange={e => onChange(e.target.value)} className="w-full h-8 px-2 text-sm border border-[#E5E5E5] bg-white focus:outline-none focus:border-[#0055FF]">
          {options.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : editing ? (
        <Input type={type || "text"} value={value || ""} onChange={e => onChange(e.target.value)} className="rounded-none h-8 text-sm" />
      ) : (
        <div className="text-sm text-[#0A0A0A] border-b border-[#F5F5F5] pb-1 break-words leading-tight min-h-[20px]">
          {value || <span className="text-[#A3A3A3] italic">—</span>}
        </div>
      )}
    </div>
  );
}

// Session 60 — First + Last name side-by-side inputs for every client contact.
// Read-only view collapses to a single "First Last" line so the overview panel
// stays compact.
function NameSplit({ firstValue, lastValue, editing, onChangeFirst, onChangeLast, testIdPrefix }) {
  const combined = [firstValue, lastValue].filter(Boolean).join(" ");
  if (!editing) {
    return (
      <div>
        <Label className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#A3A3A3] block mb-0.5">Name</Label>
        <div className="text-sm text-[#0A0A0A] border-b border-[#F5F5F5] pb-1 break-words leading-tight min-h-[20px]" data-testid={`${testIdPrefix}-name-display`}>
          {combined || <span className="text-[#A3A3A3] italic">—</span>}
        </div>
      </div>
    );
  }
  return (
    <div className="grid grid-cols-2 gap-2">
      <div>
        <Label className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#A3A3A3] block mb-0.5">First Name</Label>
        <Input
          value={firstValue || ""}
          onChange={e => onChangeFirst(e.target.value)}
          className="rounded-none h-8 text-sm"
          data-testid={`${testIdPrefix}-first-name-input`}
        />
      </div>
      <div>
        <Label className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#A3A3A3] block mb-0.5">Last Name</Label>
        <Input
          value={lastValue || ""}
          onChange={e => onChangeLast(e.target.value)}
          className="rounded-none h-8 text-sm"
          data-testid={`${testIdPrefix}-last-name-input`}
        />
      </div>
    </div>
  );
}

function Toggle({ value, onClick, disabled }) {
  return (
    <button onClick={onClick} disabled={disabled} className={`inline-flex items-center justify-center w-10 h-5 ${value ? "bg-[#16A34A]" : "bg-[#E5E5E5]"} ${disabled ? "opacity-50 cursor-not-allowed" : ""} transition-colors`}>
      <div className={`w-4 h-4 bg-white transform transition-transform ${value ? "translate-x-2.5" : "-translate-x-2.5"}`}></div>
    </button>
  );
}


/* ───────── eBA → Pulse usage sync widget ───────── */
function EbaUsagePanel({ clientId, isAdmin }) {
  const [data, setData] = useState(null);
  const [showKey, setShowKey] = useState(false);

  const reload = async () => {
    try {
      const r = await api.get(`/integrations/eba/tenants/${clientId}/usage`);
      setData(r.data);
    } catch { /* ignore */ }
  };
  useEffect(() => { reload(); }, [clientId]);
  if (!data) return null;

  const copyKey = async () => {
    if (!data.eba_api_key) return;
    try {
      await navigator.clipboard.writeText(data.eba_api_key);
      toast.success("API key copied");
    } catch { toast.error("Copy failed"); }
  };
  const rotate = async () => {
    if (!window.confirm("Rotate the eBA API key? The eBA installation will stop syncing until it's updated with the new key.")) return;
    try {
      await api.post(`/integrations/eba/tenants/${clientId}/regenerate-key`);
      toast.success("Key rotated");
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Rotate failed"); }
  };

  const modulesActive = (data.modules || []).filter(m => m.active).length;
  const modulesTotal = (data.modules || []).length;

  return (
    <div className="border border-[#E5E5E5] bg-white p-5 mb-5" data-testid="eba-usage-panel">
      <div className="flex items-baseline justify-between gap-4 mb-3 flex-wrap">
        <div>
          <h2 className="text-lg font-black tracking-tight">eBA Usage (synced)</h2>
          <p className="text-xs text-[#525252] mt-0.5">
            Snapshot pushed by the client's eBA installation to <code className="bg-[#F5F5F5] px-1">POST /api/integrations/eba/tenant-usage</code>.
          </p>
        </div>
        <div className="text-right">
          <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#A3A3A3]">Last sync</div>
          <div className="text-xs text-[#525252] tabular-nums">
            {data.last_sync_at ? formatLocal(data.last_sync_at) : "Never"}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Stat label="Active users" value={data.user_count ?? "—"} testId="eba-user-count" />
        <Stat label="Modules in use" value={modulesTotal ? `${modulesActive}/${modulesTotal}` : "—"} testId="eba-module-count" />
        <Stat label="History entries" value={(data.history || []).length} testId="eba-history-count" />
      </div>

      {(data.modules || []).length > 0 && (
        <div className="mt-4">
          <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#A3A3A3] mb-1.5">Modules</div>
          <div className="flex flex-wrap gap-1.5">
            {data.modules.map((m, i) => (
              <span key={i} className={`text-[10px] font-bold uppercase px-2 py-0.5 ${m.active ? "bg-[#F0FDF4] text-[#16A34A]" : "bg-[#F5F5F5] text-[#A3A3A3]"}`}>
                {m.name}{m.user_count != null && ` · ${m.user_count}`}
              </span>
            ))}
          </div>
        </div>
      )}

      {isAdmin && (
        <div className="mt-4 pt-3 border-t border-[#E5E5E5]">
          <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
            <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#525252]">eBA API key</span>
            <div className="flex gap-2">
              <button onClick={() => setShowKey(s => !s)}
                      className="text-[10px] font-bold uppercase tracking-wider text-[#0055FF] hover:underline"
                      data-testid="eba-toggle-key">
                {showKey ? "Hide" : "Reveal"}
              </button>
              <button onClick={copyKey}
                      className="text-[10px] font-bold uppercase tracking-wider text-[#0055FF] hover:underline"
                      data-testid="eba-copy-key">
                Copy
              </button>
              <button onClick={rotate}
                      className="text-[10px] font-bold uppercase tracking-wider text-[#DC2626] hover:underline"
                      data-testid="eba-rotate-key">
                Rotate
              </button>
            </div>
          </div>
          <code className="block text-[11px] bg-[#F5F5F5] px-2 py-1.5 font-mono break-all" data-testid="eba-api-key-display">
            {showKey ? data.eba_api_key : "•".repeat(44)}
          </code>
          <p className="text-[10px] text-[#A3A3A3] mt-1">
            Paste this into the eBA installation's Pulse integration settings. Include as <code>X-EBA-API-Key</code> header.
          </p>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, testId }) {
  return (
    <div className="border border-[#E5E5E5] p-3" data-testid={testId}>
      <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#A3A3A3]">{label}</div>
      <div className="text-2xl font-black tabular-nums mt-0.5" style={{ fontFamily: "'Cabinet Grotesk',sans-serif" }}>{value}</div>
    </div>
  );
}
